import { Box, Flex } from '@chakra-ui/react';
import { ReactNode, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Toolbar } from './Toolbar';
import { StatusBar } from './StatusBar';
import { useViewStore } from '../../state/commandAuxStores';
import { DocStripHostContext } from '../../workspaces/docStripHost';

import { ViewCubeOptionsDialog } from '../viewport';
import { HelpDialog, HelpCenterModal } from '../help';
import { ManageDialogsHost } from '../manage';
import { CollabDialogsHost } from '../collaborate/CollabDialogsHost';
import { CollabRoomDialog } from '../collaboration';
import { PluginManagerDialog } from '../plugins';

interface LayoutProps {
    children: ReactNode;
}

/**
 * Main Layout Component - Yap
 * 
 * Provides the application shell with:
 * - Top Toolbar (Tools, view controls)
 * - Bottom Status Bar (Coordinates, tool info)
 * - Center Content Area (mode surface)
 *
 * Side panels are NOT rendered here anymore: every workbench
 * (BIM / 2D / 3D / Sketch) mounts the shared panel shell (@suite/panels)
 * and its dock strips render the workbench's own registered panels.
 */
import { SuiteTopbar } from "../SuiteTopbar";
import { MenuBar } from "./ribbon/MenuBar";
import { CadRibbonShell } from "./ribbon/CadRibbonShell";

export function Layout({ children }: LayoutProps) {
    // RIBBON/MENUBAR parity flag from the command engine: when off, the top
    // tool strip is hidden (the store keeps the state, so MENUBAR toggles
    // back). Previously these runner events had no listener anywhere.
    const ribbonVisible = useViewStore((s) => s.ribbonVisible);
    // Faz 3.6: the classic 11-menu bar row (MENUBAR 0/1).
    const menubarVisible = useViewStore((s) => s.menubarVisible);
    // CLEANSCREEN (Faz 4.1): the whole chrome collapses — only the mounted
    // workbench (viewport + command line) and the document strip remain.
    const cleanScreen = useViewStore((s) => s.cleanScreen);
    const location = useLocation();
    const navigate = useNavigate();
    const isHome = location.pathname === '/';
    const isBimMode = location.pathname === '/bim';
    const isSketchMode = location.pathname === '/sketch';
    // 2D workbench: the CAD ribbon replaces the classic strip (Faz 3.2);
    // every other drafting surface keeps the shared Toolbar.
    const is2dMode = location.pathname === '/2d';
    // The document tab strip's chrome slot: the shared strip portals here
    // (WorkspaceShell stripPortalTarget), so it renders in the app chrome
    // — directly under the suite top bar — identically in every view mode.
    const [docStripHost, setDocStripHost] = useState<HTMLDivElement | null>(null);

    // WORKSPACE command: route navigation listener (view.workspace runner).
    useEffect(() => {
        const onSetWorkspace = (e: Event) => {
            const target = (e as CustomEvent<{ workspace?: string }>).detail?.workspace;
            if (!target) return;
            const route = ['bim', '2d', '3d', 'sketch'].includes(target) ? target : null;
            if (route) navigate(`/${route}`);
        };
        window.addEventListener('yap:set-workspace', onSetWorkspace);
        return () => window.removeEventListener('yap:set-workspace', onSetWorkspace);
    }, [navigate]);

    if (isHome) {
        return (
            <DocStripHostContext.Provider value={null}>
                <Box h="100%" w="100%" overflow="hidden" data-suite-selectable>
                    {children}
                </Box>
            </DocStripHostContext.Provider>
        );
    }

    return (
        <DocStripHostContext.Provider value={docStripHost}>
            <Flex h="100%" w="100%" direction="column" bg="surface.300">
                {/* The suite's served top bar — the app toolbar below stays. */}
                <SuiteTopbar />
                {/* Document tab strip host — the shared multi-document strip
                    portals in here, so it stays visible in every view mode
                    (collapses to zero height while it hosts nothing). */}
                <Box
                    ref={setDocStripHost}
                    className="yap-doc-strip-host"
                    data-yap-doc-strip-host
                />
            {/* Classic menu bar (MENUBAR 0/1, Faz 3.6) — drafting shells
                only; mounts above whichever tool strip is active. */}
            {menubarVisible && !isBimMode && !isSketchMode && !cleanScreen && <MenuBar />}

            {/* Top tool strip. 2D: ribbon shell when RIBBON is on, the
                classic strip when off (Faz 3.2). BIM drops the strip
                entirely; Sketch brings its own SketchToolbar. CLEANSCREEN
                collapses both (Faz 4.1). */}
            {is2dMode && ribbonVisible && !cleanScreen && <CadRibbonShell />}
            {!is2dMode && ribbonVisible && !isBimMode && !isSketchMode && !cleanScreen && <Toolbar />}

            {/* Main Content Area — the mounted workbench renders its own
                side panels through the shared panel shell's dock strips */}
            <Flex flex="1" overflow="hidden">
                {/* Center - Mode surface */}
                <Box
                    flex="1"
                    position="relative"
                    bg="bg.canvas"
                    overflow="hidden"
                    data-tour-id="viewport"
                >
                    {children}
                </Box>
            </Flex>

            {/* Bottom Status Bar (hidden under CLEANSCREEN) */}
            {!cleanScreen && <StatusBar />}

            {/* Global Dialogs */}
            <ViewCubeOptionsDialog />
            <HelpDialog />
            <HelpCenterModal />
            <ManageDialogsHost />
            <CollabDialogsHost />
            <CollabRoomDialog />
            <PluginManagerDialog />
            </Flex>
        </DocStripHostContext.Provider>
    );
}
